import React, { useState, useEffect } from 'react';
import {
  SignalIcon,
  ArrowPathIcon,
  PencilIcon,
  InformationCircleIcon,
  PaperAirplaneIcon
} from '@heroicons/react/24/outline';
import { api } from '../services/api';
import toast from '../utils/toast';
import SearchableSelect from '../components/SearchableSelect';

const SetStatusPage = () => {
  const [agents, setAgents] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState('');
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    fetchAgents();
  }, []);

  const fetchAgents = async () => {
    try {
      setLoading(true);
      const response = await api.getWhatsAppClients();
      setAgents(response.data.clients || []);
    } catch (error) {
      console.error('Error fetching WhatsApp clients:', error);
      toast.error('Failed to load WhatsApp clients');
    } finally {
      setLoading(false);
    }
  };

  // Prepare WhatsApp clients data for SearchableSelect
  const agentOptions = agents && agents.clients
    ? Object.values(agents.clients)
        .filter(client => client.status === 'active')
        .map((client) => ({
          value: client.id,
          label: client.agent_name || client.display_name || `Client ${client.client_id}`,
          description: client.whatsapp_number || client.client_id,
          icon: '📱',
          port: client.port,
          webhookApiKey: client.webhook_api_key || client.api_key,
          clientId: client.client_id
        }))
    : [];

  const handleSetStatus = async () => {
    if (!selectedAgent || !status) {
      toast.error('Please select an agent and enter a status');
      return;
    }

    setUpdating(true);
    try {
      const agent = agents.find(a => a.id === selectedAgent);
      if (!agent) {
        toast.error('Agent not found');
        return;
      }

      // This would need the actual API endpoint for setting status
      toast.info('Status update functionality coming soon');
      setStatus('');
    } catch (error) {
      console.error('Error setting status:', error);
      toast.error('Failed to update status');
    } finally {
      setUpdating(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-whatsapp-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading your agents...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Header */}
        <div className="bg-white shadow rounded-lg mb-6">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-indigo-100 rounded-lg">
                  <SignalIcon className="h-8 w-8 text-indigo-600" />
                </div>
                <div>
                  <h1 className="text-2xl font-semibold text-gray-900">Set WhatsApp Status</h1>
                  <p className="text-sm text-gray-600">Update your WhatsApp agent's status message</p>
                </div>
              </div>
              <button
                onClick={fetchAgents}
                className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors"
              >
                <ArrowPathIcon className="h-4 w-4 mr-2" />
                Refresh
              </button>
            </div>
          </div>
        </div>

        {/* Form */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center">
              <PencilIcon className="h-5 w-5 text-gray-400 mr-2" />
              <h2 className="text-lg font-medium text-gray-900">Status Configuration</h2>
            </div>
          </div>

          <form onSubmit={(e) => { e.preventDefault(); handleSetStatus(); }} className="px-6 py-6 space-y-6">

            {/* Agent Selection */}
            <div className="space-y-6">
              <h3 className="text-base font-medium text-gray-900 border-b border-gray-200 pb-2">
                Select WhatsApp Agent
              </h3>

              <SearchableSelect
                label="WhatsApp Agent"
                name="selectedAgent"
                options={agentOptions}
                value={selectedAgent}
                onChange={(e) => setSelectedAgent(e.target.value)}
                placeholder="Search and select a WhatsApp agent..."
                isRequired={true}
                isSearchable={true}
                isClearable={true}
              />

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex">
                  <InformationCircleIcon className="h-5 w-5 text-blue-400" />
                  <div className="ml-3">
                    <h4 className="text-sm font-medium text-blue-800">WhatsApp Agent Selection</h4>
                    <p className="mt-1 text-sm text-blue-700">
                      Choose a connected WhatsApp agent to update its status. Only agents with active connections are available.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Status Details */}
            <div className="space-y-6">
              <h3 className="text-base font-medium text-gray-900 border-b border-gray-200 pb-2">
                Status Message
              </h3>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Status Text <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <textarea
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                    rows={4}
                    className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-whatsapp-500 focus:border-whatsapp-500 resize-none"
                    placeholder="Available for business inquiries..."
                    required
                  />
                </div>
                <div className="flex justify-between items-center mt-2">
                  <p className="text-sm text-gray-500">
                    Your WhatsApp status will be visible to your contacts
                  </p>
                  <span className="text-sm text-gray-500">
                    {status.length} characters
                  </span>
                </div>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <div className="flex">
                  <InformationCircleIcon className="h-5 w-5 text-amber-400" />
                  <div className="ml-3">
                    <h4 className="text-sm font-medium text-amber-800">Status Visibility</h4>
                    <p className="mt-1 text-sm text-amber-700">
                      Your status message will be displayed to all your WhatsApp contacts. Keep it professional and relevant to your business.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={() => {
                  setSelectedAgent('');
                  setStatus('');
                }}
                disabled={updating}
                className="px-6 py-3 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Clear Form
              </button>

              <button
                type="submit"
                disabled={!selectedAgent || !status || updating}
                className="px-8 py-3 text-sm font-medium text-white bg-gradient-to-r from-whatsapp-500 to-green-600 rounded-lg hover:from-whatsapp-600 hover:to-green-700 transform hover:scale-[1.02] transition-all duration-200 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {updating ? (
                  <>
                    <ArrowPathIcon className="h-4 w-4 mr-2 animate-spin" />
                    Updating Status...
                  </>
                ) : (
                  <>
                    <PaperAirplaneIcon className="h-4 w-4 mr-2" />
                    Update Status
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default SetStatusPage;
